import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./CentreInformation.module.sass";
import Card from "../../../../components/Card";
import TextInput from "../../../../components/TextInput";
import Switch from "../../../../components/Switch";
import { searchCenters, getCenterById } from "../../../../services/center.service";

const CentreInformations = ({ className, formation, onSelectCenter, onSwitchChange, onSubmit, loading }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Charger le centre si center_id existe dans la formation
  useEffect(() => {
    const loadCenter = async () => {
      if (formation?.center_id && !selectedCenter) {
        try {
          const center = await getCenterById(formation.center_id);
          if (center) {
            setSelectedCenter(center);
            setSearchQuery(center.name || '');
          }
        } catch (error) {
          console.error('Erreur lors du chargement du centre:', error);
        }
      }
    };

    loadCenter();
  }, [formation?.center_id, selectedCenter]);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchCenters = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchCenters(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des centres:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };

    fetchCenters();
  }, [searchQuery]);

  // Gérer la sélection d'un centre
  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
    setSearchQuery(center.name);
    setShowResults(false);

    // Transmettre le centre sélectionné au composant parent avec les données d'adresse
    if (onSelectCenter) {
      // Si place_Identical_training est activé, on envoie aussi les données d'adresse
      if (formation?.place_Identical_training) {
        const centerWithAddressData = {
          ...center,
          addressData: {
            address: center.address || '',
            address_complement: center.address_complement || '',
            zip_code: center.zip_code || '',
            city: center.city || '',
            department: center.department || '',
            country: center.country || 'France'
          }
        };
        onSelectCenter(centerWithAddressData);
      } else {
        onSelectCenter(center);
      }
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedCenter && e.target.value !== selectedCenter.name) {
      setSelectedCenter(null);
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title="Centre de formation"
      classTitle="title-blue"
    >
      <div className={styles.fieldset}>
        <div className={styles.searchContainer}>
          <TextInput
            className={styles.field}
            label="Rechercher un centre de formation"
            name="center-search"
            type="text"
            placeholder="Nom du centre (min. 2 caractères)"
            value={searchQuery}
            onChange={handleSearchChange}
            required
          />

          {showResults && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              {searchResults.map((center, index) => (
                <div
                  key={center.id || index}
                  className={styles.resultItem}
                  onClick={() => handleSelectCenter(center)}
                >
                  <div className={styles.resultContent}>
                    <img
                      src={center.logoUrl || "/images/content/school_logo.png"}
                      alt={center.name}
                      className={styles.companyLogo}
                    />
                    <div className={styles.companyInfo}>
                      <div className={styles.companyName}>{center.name}</div>
                      <div className={styles.companyDetails}>
                        <span>
                          {center.address}
                          {center.zip_code && center.city && `, ${center.zip_code} ${center.city}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && (
            <div className={styles.noResults}>
              Aucun centre de formation trouvé
            </div>
          )}
        </div>

        {selectedCenter && (
          <div className={styles.selectedCompanyDetails}>
            <h3>Détails du centre sélectionné</h3>
            <div className={styles.detailRow}>
              <span className={styles.label}>Nom:</span>
              <span className={styles.value}>{selectedCenter.name}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Adresse:</span>
              <span className={styles.value}>
                {selectedCenter.address}
                {selectedCenter.address_complement && `, ${selectedCenter.address_complement}`}
                {selectedCenter.zip_code && selectedCenter.city && `, ${selectedCenter.zip_code} ${selectedCenter.city}`}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Téléphone:</span>
              <span className={styles.value}>{selectedCenter.phone || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{selectedCenter.email || 'Non renseigné'}</span>
            </div>
          </div>
        )}


        {selectedCenter && (
          <>
            <div className={styles.field} style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
              <span style={{ marginRight: '10px' }}>Lieu de formation identique à l'adresse du centre :</span>
              <Switch
                name="place_Identical_training"
                value={formation?.place_Identical_training || false}
                onChange={(value) => {
                  // Quand on change la valeur du switch, on met à jour aussi les données d'adresse
                  onSwitchChange('place_Identical_training', value);

                  // Si on active le switch, on copie les données du centre
                  if (value && selectedCenter) {
                    const centerWithAddressData = {
                      ...selectedCenter,
                      addressData: {
                        address: selectedCenter.address || '',
                        address_complement: selectedCenter.address_complement || '',
                        zip_code: selectedCenter.zip_code || '',
                        city: selectedCenter.city || '',
                        department: selectedCenter.department || '',
                        country: selectedCenter.country || 'France'
                      }
                    };
                    onSelectCenter(centerWithAddressData);
                  }
                }}
                className={styles.switch}
              />
            </div>

          </>
        )}
      </div>
    </Card>
  );
};

export default CentreInformations;
